import { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, Input, InputGroup, InputLeftAddon, InputRightAddon, Select } from "@chakra-ui/react";
import { AuthWrapper } from "../components/AuthWrapper";
import { StoreBuildingLoader } from "../components/StoreBuildingLoader";
import { FeatureSlider } from "../components/FeatureSlider";
import PhoneNumberInput from "../components/PhoneNumberComponent/PhoneNumberInput";
import { COUNTRIES } from "../components/PhoneNumberComponent/countries";
import http from "../connection/connect";
import { startSession } from "../helpers/global";
import { trackEvent } from "../helpers/tracking";
import "../styles/google.scss";

const POLL_MS = 2500;

/**
 * Second half of "Continue with Google" for a NEW e-mail.
 *
 * The account does not exist yet; storak-auth holds the Google profile behind
 * `?ticket=`. This page blocks until the four store details are given (store
 * name, currency, phone, store address = subdomain), then shows the building
 * screen and polls until the store is created AND answering, and finally signs
 * the merchant into the portal and their store admin in one go.
 */
const GoogleComplete = ({ t, i18n }) => {
  const params = new URLSearchParams(window.location.search);
  const ticket = params.get("ticket") || "";
  const language = String(i18n.language || "").toLowerCase().startsWith("ar") ? "ar" : "en";

  const [profile, setProfile] = useState(null);
  const [fatal, setFatal] = useState("");
  const [storeName, setStoreName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [currency, setCurrency] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [building, setBuilding] = useState(false);
  const [progress, setProgress] = useState({ state: "building", step: "waiting" });
  const [failed, setFailed] = useState("");
  const timer = useRef(null);
  const leaving = useRef(false);

  const countryOptions = COUNTRIES.map(({ name, iso }) => ({ label: name, value: iso })).filter(
    ({ value }) => value !== "ISR"
  );

  // The page language follows the language the merchant started in.
  useEffect(() => {
    const lng = params.get("lng");
    if ((lng === "ar" || lng === "en") && !String(i18n.language).startsWith(lng)) {
      i18n.changeLanguage(lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await http.get(`api/oauth/google/signup/${encodeURIComponent(ticket)}/status`);
      if (data.state === "ready") {
        setProgress({ state: "ready" });
        startSession(data.session);
        trackEvent("sign_up", undefined, { method: "google", language, currency: data.currency || currency });
        setTimeout(() => {
          leaving.current = true;
          window.location.href = data.url;
        }, 1400);
        return;
      }
      if (data.state === "failed") {
        setFailed(data.message || t("google.failed", "We could not create your store."));
        return;
      }
      setProgress({ state: data.state, step: data.step });
    } catch (error) {
      if (error.response?.status === 410) {
        setFailed(error.response.data?.message);
        return;
      }
      // Network blip: keep polling.
    }
    timer.current = setTimeout(poll, POLL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  useEffect(() => {
    if (!ticket) {
      setFatal(t("google.expired", "This sign-in has expired. Please continue with Google again."));
      return;
    }
    http
      .get(`api/oauth/google/signup/${encodeURIComponent(ticket)}`)
      .then(({ data }) => {
        setProfile(data);
        // Already submitted (page reloaded while building): resume the wait.
        if (data.queued) {
          setBuilding(true);
          poll();
        }
      })
      .catch((error) =>
        setFatal(
          error.response?.data?.message ||
            t("google.expired", "This sign-in has expired. Please continue with Google again.")
        )
      );
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A blocking step: warn before leaving while the store is being built.
  useEffect(() => {
    if (!building) return undefined;
    const guard = (e) => {
      if (leaving.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [building]);

  const validate = () => {
    const next = {};
    if (!storeName.trim()) next.storeName = t("google.required", "Required");
    if (!currency) next.currency = t("google.required", "Required");
    if (!/^\+?(?:[\s().-]*\d){7,15}[\s().-]*$/.test(phoneNumber || "")) next.phoneNumber = t("google.phoneInvalid", "Enter a valid phone number");
    if (!/^[a-z0-9]{3,40}$/.test(username)) next.username = t("google.addressInvalid", "3–40 letters or numbers");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await http.post("api/oauth/google/signup", {
        ticket,
        storeName: storeName.trim(),
        currency,
        phoneNumber,
        username,
      });
      trackEvent("google_signup_details", undefined, { language, currency });
      setBuilding(true);
      poll();
    } catch (error) {
      const message = error.response?.data?.message;
      const list = Array.isArray(message) ? message : [message];
      const next = {};
      list.filter(Boolean).forEach((m) => {
        const text = String(m);
        if (/username|address|subdomain|exists|unique/i.test(text) && !/email/i.test(text)) next.username = t("google.addressTaken", "This store address is taken, try another one");
        else if (/phone/i.test(text)) next.phoneNumber = text;
        else if (/currency/i.test(text)) next.currency = text;
        else if (/store name/i.test(text)) next.storeName = text;
        else next.form = text;
      });
      if (error.response?.status === 410) {
        setFatal(error.response.data.message);
      }
      setErrors(Object.keys(next).length ? next : { form: t("google.failed", "We could not create your store.") });
    } finally {
      setSubmitting(false);
    }
  };

  if (building) {
    return (
      <StoreBuildingLoader
        storeName={storeName || profile?.name}
        state={progress.state}
        step={progress.step}
        failed={failed}
        onRetry={failed ? () => (window.location.href = "/register") : null}
      />
    );
  }

  if (fatal) {
    return (
      <AuthWrapper header={t("google.expiredTitle", "Let's try that again")} locked>
        <Box className="sp-field-error" mb={4}>{fatal}</Box>
        <Button width="100%" onClick={() => (window.location.href = "/register")}>
          {t("google.startOver", "Continue with Google again")}
        </Button>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper
      locked
      hero={<FeatureSlider />}
      heroTitle={t("google.heroTitle", "Everything you need to sell, in one place")}
      heroText={t("google.heroTextForm", "Four details and your store is live, with all of this switched on.")}
      header={t("google.completeTitle", "Almost there — set up your store")}
      subtitle={t("google.completeSub", "These details are required to create your store. It takes one minute.")}
    >
      {profile ? (
        <div className="sp-google-who">
          {profile.picture ? <img src={profile.picture} alt="" referrerPolicy="no-referrer" /> : null}
          <div>
            <strong>{profile.name}</strong>
            <span>{profile.email}</span>
          </div>
        </div>
      ) : null}

      <form onSubmit={submit} noValidate>
        <div className="sp-field-label">{t("google.storeName", "Store name")}</div>
        <Input
          id="StoreName"
          type="text"
          maxLength={80}
          autoFocus
          placeholder={t("registerPage.storeName", "Store name")}
          value={storeName}
          isInvalid={Boolean(errors.storeName)}
          onChange={(e) => {
            const value = e.target.value;
            setStoreName(value);
            if (!usernameEdited) setUsername(value.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 40));
          }}
        />
        {errors.storeName ? <div className="sp-field-error">{errors.storeName}</div> : null}

        <div className="sp-field-label">{t("google.currency", "Currency")}</div>
        <Select
          id="Currency"
          value={currency}
          isInvalid={Boolean(errors.currency)}
          placeholder={t("registerPage.selectCurrency")}
          onChange={(e) => setCurrency(e.target.value)}
        >
          <option value="SAR">{t("registerPage.SAR")}</option>
          <option value="USD">{t("registerPage.USD")}</option>
          <option value="EGP">{t("registerPage.EGP")}</option>
          <option value="EUR">{t("registerPage.EUR")}</option>
        </Select>
        {errors.currency ? <div className="sp-field-error">{errors.currency}</div> : null}

        <div className="sp-field-label">{t("google.phone", "Phone number")}</div>
        <PhoneNumberInput
          country={"EGY"}
          value={phoneNumber}
          options={countryOptions}
          lang={i18n.language}
          placeholder={t("registerPage.phoneNumber")}
          onChange={(value) => setPhoneNumber(value)}
        />
        {errors.phoneNumber ? <div className="sp-field-error">{errors.phoneNumber}</div> : null}

        <div className="sp-field-label">{t("registerPage.storeAddress", "Your store address")}</div>
        <Box dir="ltr">
          <InputGroup>
            <InputLeftAddon>https://</InputLeftAddon>
            <Input
              id="Username"
              type="text"
              maxLength={40}
              value={username}
              isInvalid={Boolean(errors.username)}
              placeholder={t("registerPage.username")}
              onChange={(e) => {
                setUsernameEdited(true);
                setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 40));
              }}
            />
            <InputRightAddon>.storkplus.com</InputRightAddon>
          </InputGroup>
        </Box>
        {errors.username ? <div className="sp-field-error">{errors.username}</div> : null}

        {errors.form ? <div className="sp-field-error" style={{ marginTop: 14 }}>{errors.form}</div> : null}

        <Button type="submit" width="100%" mt={7} isLoading={submitting} loadingText={t("google.create", "Create my store")}>
          {t("google.create", "Create my store")}
        </Button>
      </form>
    </AuthWrapper>
  );
};

export default withTranslation()(GoogleComplete);
